/** The portion of a flow time bucket needed to decide how a chart renders it. */
export type FlowChartBucket = {
  recordCount: number;
  bucketDurationSeconds?: number;
};

const resolutionSeconds = (resolution: string | undefined): number | null => {
  switch (resolution) {
    case '1m': return 60;
    case '2m': return 120;
    case '5m': return 300;
    case '10m': return 600;
    default: return null;
  }
};

/**
 * Returns whether an empty bucket represents only a clipped request edge.
 * Complete empty buckets retain a measured observed rate of zero; a chart gap
 * is reserved for an empty first or final bucket shorter than its nominal
 * server-selected resolution.
 */
export const isPartialEmptyEdgeBucket = (
  bucket: FlowChartBucket | undefined,
  index: number,
  bucketCount: number,
  resolution: string | undefined
): boolean => {
  const nominalDuration = resolutionSeconds(resolution);
  const duration = bucket?.bucketDurationSeconds;
  return bucket?.recordCount === 0
    && (index === 0 || index === bucketCount - 1)
    && nominalDuration != null
    && typeof duration === 'number'
    && duration > 0
    && duration < nominalDuration;
};

/**
 * Converts a server-returned bucket rate to a chart point. Missing buckets and
 * genuinely empty partial edge buckets become gaps; every finite complete
 * bucket rate, including zero, remains visible on the chart.
 */
export const chartRateForBucket = (
  bucket: FlowChartBucket | undefined,
  index: number,
  bucketCount: number,
  resolution: string | undefined,
  rate: number | undefined
): number | null => {
  if (!bucket || isPartialEmptyEdgeBucket(bucket, index, bucketCount, resolution)) return null;
  return typeof rate === 'number' && Number.isFinite(rate) ? rate : null;
};
